import { RegistrationStatus } from '@/enums/compliance'
import i18n from '@/libs/i18n'
import type { SelectOption, TagItem } from '@/types/Common'

/**
 * The colour of a filing.
 *
 * Pending is neutral rather than amber: a registration waiting its turn in the queue is
 * the ordinary state of every filing for its first minute, and a screen that paints the
 * ordinary state as a warning teaches the desk to ignore the colour.
 */
export const getRegistrationStatus = (status: string): TagItem => {
    const map: Record<string, TagItem> = {
        [RegistrationStatus.Pending]:   { label: i18n.global.t('registration_status.pending'),   color: 'neutral' },
        [RegistrationStatus.Sent]:      { label: i18n.global.t('registration_status.sent'),      color: 'info' },
        [RegistrationStatus.Confirmed]: { label: i18n.global.t('registration_status.confirmed'), color: 'success' },
        [RegistrationStatus.Failed]:    { label: i18n.global.t('registration_status.failed'),    color: 'error' },
        [RegistrationStatus.Cancelled]: { label: i18n.global.t('registration_status.cancelled'), color: 'warning' },
    }

    return map[status] ?? { label: status, color: 'neutral' }
}

export const registrationStatusOptions = (): SelectOption[] =>
    Object.values(RegistrationStatus).map(value => ({
        label: i18n.global.t(`registration_status.${value}`),
        value,
    }))

/**
 * One field eTurizem is short of, in the desk's own words.
 *
 * The service answers keys — `date_of_birth` — because it is imported by three layers and
 * knows no language; the translation is here so that the panel and the toast that follows
 * a refusal name the same field the same way. A key nobody has a word for is printed as
 * it arrived rather than swallowed: a field added to the service before it is added here
 * must still send somebody to the right box.
 */
export const registrationMissingLabel = (key: string): string => {
    const label = `registration.missing.${key}`

    return i18n.global.te(label) ? i18n.global.t(label) : key
}

/** „datum rojstva, številka dokumenta“ — the fields of a refusal as one line. */
export const registrationMissingLabels = (keys: string[] | null | undefined): string =>
    (keys ?? []).map(registrationMissingLabel).join(', ')
